import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { ReviewCriterionRow } from "@/hooks/use-review-criteria";

/**
 * Pick questions to put on a form — EPIC-047.
 *
 * A list you can tick several of and add in one go, rather than a picker that
 * closes after each. Building a form is adding five or six questions at once;
 * the one-at-a-time control turned that into five round trips.
 *
 * "Write a new one" is offered here as well as on the section header, because
 * the moment you discover the question you want does not exist is the moment
 * you are looking for it in this list.
 */
export function ReviewQuestionPicker({
  open,
  onOpenChange,
  options,
  onAdd,
  onWriteNew,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Questions not already on the form. */
  options: ReviewCriterionRow[];
  /**
   * Called ONCE with everything ticked, never once per question.
   *
   * It used to be called in a loop, and each call appended to the list as it
   * stood when the dialog rendered — so ticking five questions added the last
   * one and silently dropped four. The fix is the signature: one call cannot
   * race itself.
   */
  onAdd: (questions: ReviewCriterionRow[]) => void;
  onWriteNew: () => void;
}) {
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);

  const needle = search.trim().toLowerCase();
  const shown = needle
    ? options.filter(
        (q) =>
          q.title.toLowerCase().includes(needle) ||
          (q.helpText ?? "").toLowerCase().includes(needle),
      )
    : options;

  function add() {
    // Ordered as the list shows them, not as they were ticked: the person is
    // reading a list, and the form should come back in the order they saw.
    const picked = options.filter((q) => chosen.includes(q.id));
    if (picked.length > 0) onAdd(picked);
    setChosen([]);
    setSearch("");
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setChosen([]);
          setSearch("");
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add questions</DialogTitle>
          <DialogDescription>
            Tick everything you want on this form. You can change the order afterwards.
          </DialogDescription>
        </DialogHeader>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search questions…"
          aria-label="Search questions"
        />

        <div className="max-h-80 overflow-y-auto">
          {shown.length === 0 ? (
            <EmptyState
              title={options.length === 0 ? "Every question is already on this form" : "No matches"}
              description={
                options.length === 0
                  ? "Write a new one if you need something else asked."
                  : "Nothing matches that. Write a new question instead?"
              }
            />
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((question) => {
                const ticked = chosen.includes(question.id);
                return (
                  <li key={question.id}>
                    <button
                      type="button"
                      aria-pressed={ticked}
                      onClick={() =>
                        setChosen(
                          ticked
                            ? chosen.filter((id) => id !== question.id)
                            : [...chosen, question.id],
                        )
                      }
                      className={`flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-muted/40 ${
                        ticked ? "bg-muted/60" : ""
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-sm">{question.title}</span>
                          {question.categoryName && (
                            <StatusPill tone="neutral" label={question.categoryName} />
                          )}
                          {question.isDefault && <StatusPill tone="info" label="Standard" />}
                        </span>
                        {question.helpText && (
                          <span className="mt-0.5 block text-muted-foreground text-sm">
                            {question.helpText}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-muted-foreground text-xs">
                        {ticked ? "Added" : "Add"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={onWriteNew}>
            Write a new question
          </Button>
          <Button disabled={chosen.length === 0} onClick={add}>
            {chosen.length === 0
              ? "Add"
              : `Add ${chosen.length} ${chosen.length === 1 ? "question" : "questions"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
